import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import { buildQueryString } from "@/services/baseClient";
import type {
  ApiResponse,
  Vehicle,
  VehicleModel,
  CreateVehicleRequest,
  CreateVehicleModelRequest,
  VehicleMaintenance,
  CreateVehicleMaintenanceRequest,
  VehicleIssue,
  CreateVehicleIssueRequest,
  VehicleTimelineEvent,
} from "@/types";

export function createVehiclesApi({ client }: HttpClient) {
  return {
    async getVehicles(): Promise<Vehicle[]> {
      const response: AxiosResponse<ApiResponse<Vehicle[]>> =
        await client.get("/vehicles");
      return response.data.data;
    },

    async getVehicle(id: string): Promise<Vehicle> {
      const response: AxiosResponse<ApiResponse<Vehicle>> =
        await client.get(`/vehicles/${id}`);
      return response.data.data;
    },

    async createVehicle(
      vehicleData: CreateVehicleRequest,
    ): Promise<Vehicle> {
      const response: AxiosResponse<ApiResponse<Vehicle>> =
        await client.post("/vehicles", vehicleData);
      return response.data.data;
    },

    async updateVehicle(
      id: string,
      vehicleData: Partial<CreateVehicleRequest>,
    ): Promise<Vehicle> {
      const response: AxiosResponse<ApiResponse<Vehicle>> =
        await client.put(`/vehicles/${id}`, vehicleData);
      return response.data.data;
    },

    async deleteVehicle(id: string): Promise<void> {
      await client.delete(`/vehicles/${id}`);
    },

    async getUserVehiclesCount(): Promise<number> {
      const response: AxiosResponse<{
        success: boolean;
        data: { vehiclesCount: number };
      }> = await client.get("/vehicles/stats/count");
      return response.data.data.vehiclesCount;
    },

    async getVehicleModels(params?: {
      search?: string;
      maker?: string;
      year?: number;
      yearRange?: string;
      limit?: number;
      offset?: number;
      orderBy?: "createdAt" | "year" | "maker" | "model";
      orderDirection?: "asc" | "desc";
    }): Promise<{ vehicleModels: VehicleModel[]; total: number }> {
      const queryString = buildQueryString({
        search: params?.search,
        maker: params?.maker,
        year: params?.year,
        yearRange: params?.yearRange,
        limit: params?.limit,
        offset: params?.offset,
        orderBy: params?.orderBy,
        orderDirection: params?.orderDirection,
      });

      const response: AxiosResponse<{
        success: boolean;
        data: VehicleModel[];
        total: number;
      }> = await client.get(`/vehicle-models${queryString}`);
      return {
        vehicleModels: response.data.data,
        total: response.data.total,
      };
    },

    async getVehicleModel(id: string): Promise<VehicleModel> {
      const response: AxiosResponse<ApiResponse<VehicleModel>> =
        await client.get(`/vehicle-models/${id}`);
      return response.data.data;
    },

    async createVehicleModel(
      vehicleModelData: CreateVehicleModelRequest,
    ): Promise<VehicleModel> {
      const response: AxiosResponse<ApiResponse<VehicleModel>> =
        await client.post("/vehicle-models", vehicleModelData);
      return response.data.data;
    },

    async updateVehicleModel(
      id: string,
      vehicleModelData: Partial<CreateVehicleModelRequest>,
    ): Promise<VehicleModel> {
      const response: AxiosResponse<ApiResponse<VehicleModel>> =
        await client.put(`/vehicle-models/${id}`, vehicleModelData);
      return response.data.data;
    },

    async deleteVehicleModel(id: string): Promise<void> {
      await client.delete(`/vehicle-models/${id}`);
    },

    async searchVehicleModels(
      query: string,
      limit?: number,
    ): Promise<VehicleModel[]> {
      const response: AxiosResponse<ApiResponse<VehicleModel[]>> =
        await client.get(
          `/vehicle-models/search/${encodeURIComponent(query)}${limit ? `?limit=${limit}` : ""}`,
        );
      return response.data.data;
    },

    async getVehicleModelMakers(): Promise<string[]> {
      const response: AxiosResponse<ApiResponse<string[]>> =
        await client.get("/vehicle-models/makers");
      return response.data.data;
    },

    async getVehicleModelYears(): Promise<number[]> {
      const response: AxiosResponse<ApiResponse<number[]>> =
        await client.get("/vehicle-models/years");
      return response.data.data;
    },

    async getVehicleModelsByMaker(maker: string): Promise<string[]> {
      const response: AxiosResponse<ApiResponse<string[]>> =
        await client.get(
          `/vehicle-models/makers/${encodeURIComponent(maker)}/models`,
        );
      return response.data.data;
    },

    async getVehicleMaintenance(params?: {
      vehicleId?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
      limit?: number;
      offset?: number;
      orderBy?: "performedAt" | "createdAt" | "mileage";
      orderDirection?: "asc" | "desc";
    }): Promise<{ maintenance: VehicleMaintenance[]; total: number }> {
      const queryString = buildQueryString({
        vehicleId: params?.vehicleId,
        type: params?.type,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        limit: params?.limit,
        offset: params?.offset,
        orderBy: params?.orderBy,
        orderDirection: params?.orderDirection,
      });

      const response: AxiosResponse<{
        success: boolean;
        data: VehicleMaintenance[];
        total: number;
      }> = await client.get(`/vehicle-maintenance${queryString}`);
      return {
        maintenance: response.data.data,
        total: response.data.total,
      };
    },

    async getMaintenanceRecord(id: string): Promise<VehicleMaintenance> {
      const response: AxiosResponse<ApiResponse<VehicleMaintenance>> =
        await client.get(`/vehicle-maintenance/${id}`);
      return response.data.data;
    },

    async createMaintenanceRecord(
      data: CreateVehicleMaintenanceRequest,
    ): Promise<VehicleMaintenance> {
      const response: AxiosResponse<ApiResponse<VehicleMaintenance>> =
        await client.post("/vehicle-maintenance", data);
      return response.data.data;
    },

    async updateMaintenanceRecord(
      id: string,
      data: Partial<CreateVehicleMaintenanceRequest>,
    ): Promise<VehicleMaintenance> {
      const response: AxiosResponse<ApiResponse<VehicleMaintenance>> =
        await client.put(`/vehicle-maintenance/${id}`, data);
      return response.data.data;
    },

    async deleteMaintenanceRecord(id: string): Promise<void> {
      await client.delete(`/vehicle-maintenance/${id}`);
    },

    async getVehicleMaintenanceByVehicle(
      vehicleId: string,
    ): Promise<VehicleMaintenance[]> {
      const response: AxiosResponse<ApiResponse<VehicleMaintenance[]>> =
        await client.get(`/vehicle-maintenance/vehicle/${vehicleId}`);
      return response.data.data;
    },

    async getRecentMaintenanceByVehicle(
      vehicleId: string,
      limit?: number,
    ): Promise<VehicleMaintenance[]> {
      const queryString = buildQueryString({ limit });
      const response: AxiosResponse<ApiResponse<VehicleMaintenance[]>> =
        await client.get(
          `/vehicle-maintenance/vehicle/${vehicleId}/recent${queryString}`,
        );
      return response.data.data;
    },

    async searchMaintenanceRecords(
      query: string,
      limit?: number,
    ): Promise<VehicleMaintenance[]> {
      const queryParams = limit ? `?limit=${limit}` : "";
      const encodedQuery = encodeURIComponent(query)
        .replace(/\(/g, "%28")
        .replace(/\)/g, "%29");

      const response: AxiosResponse<ApiResponse<VehicleMaintenance[]>> =
        await client.get(
          `/vehicle-maintenance/search/${encodedQuery}${queryParams}`,
        );
      return response.data.data;
    },

    async getVehicleIssues(params?: {
      vehicleId?: string;
      search?: string;
      limit?: number;
      offset?: number;
      orderBy?: "occurredAt" | "createdAt";
      orderDirection?: "asc" | "desc";
    }): Promise<{ issues: VehicleIssue[]; total: number }> {
      const queryString = buildQueryString({
        vehicleId: params?.vehicleId,
        search: params?.search,
        limit: params?.limit,
        offset: params?.offset,
        orderBy: params?.orderBy,
        orderDirection: params?.orderDirection,
      });

      const response: AxiosResponse<{
        success: boolean;
        data: VehicleIssue[];
        total: number;
      }> = await client.get(`/vehicle-issues${queryString}`);
      return { issues: response.data.data, total: response.data.total };
    },

    async getVehicleIssue(id: string): Promise<VehicleIssue> {
      const response: AxiosResponse<ApiResponse<VehicleIssue>> =
        await client.get(`/vehicle-issues/${id}`);
      return response.data.data;
    },

    async createVehicleIssue(
      data: CreateVehicleIssueRequest,
    ): Promise<VehicleIssue> {
      const response: AxiosResponse<ApiResponse<VehicleIssue>> =
        await client.post("/vehicle-issues", data);
      return response.data.data;
    },

    async updateVehicleIssue(
      id: string,
      data: Partial<CreateVehicleIssueRequest>,
    ): Promise<VehicleIssue> {
      const response: AxiosResponse<ApiResponse<VehicleIssue>> =
        await client.put(`/vehicle-issues/${id}`, data);
      return response.data.data;
    },

    async deleteVehicleIssue(id: string): Promise<void> {
      await client.delete(`/vehicle-issues/${id}`);
    },

    async getVehicleIssuesByVehicle(
      vehicleId: string,
    ): Promise<VehicleIssue[]> {
      const response: AxiosResponse<ApiResponse<VehicleIssue[]>> =
        await client.get(`/vehicle-issues/vehicle/${vehicleId}`);
      return response.data.data;
    },

    async getVehicleTimeline(
      vehicleId: string,
      params?: { limit?: number; offset?: number; type?: string },
    ): Promise<{ events: VehicleTimelineEvent[]; total: number }> {
      const queryParams = buildQueryString({
        ...(params?.limit !== undefined && { limit: params.limit }),
        ...(params?.offset !== undefined && { offset: params.offset }),
        ...(params?.type && { type: params.type }),
      });

      type TimelineResponse = {
        success: boolean;
        data: VehicleTimelineEvent[];
        total: number;
        timestamp: string;
      };

      const response: AxiosResponse<TimelineResponse> = await client.get(
        `/vehicles/${vehicleId}/timeline${queryParams}`,
      );

      return { events: response.data.data, total: response.data.total };
    },
  };
}
